import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/standartComponents/header';
// УВАГА: перевірте правильний шлях до allauth.js у вашому проекті
import { verifyEmail } from '../../lib/allauth'; 
import css from './verifyEmail.module.css';

const APP_HOME = '/mylearning';
const REDIRECT_DELAY = 3000;
const PREVIEW_STATES = ['loading', 'success', 'confirmed', 'error'];

const VerifyEmail = ({ authUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { key } = useParams(); // Отримуємо key з URL /account/verify-email/:key

  const [status, setStatus] = useState('loading');

  const requestedKey = useRef(null);

  useEffect(() => {
  const preview = new URLSearchParams(search).get('preview');
  if (PREVIEW_STATES.includes(preview)) {
    setStatus(preview);
    return;
  }
  if (!key) {
    setStatus('error');
    return;
  }
  if (requestedKey.current === key) return; // уже отправляли этот ключ
  requestedKey.current = key;

  (async () => {
    const result = await verifyEmail({ key });
    if (result.status === 200) {
      await window.refreshAuthUser?.();
      setStatus('success');
    } else if (result.status === 401) {
      setStatus('confirmed');
    } else {
      setStatus('error');
    }
  })();
}, [key, search]);

  useEffect(() => {
    if (status !== 'success') return undefined;
    const timer = setTimeout(() => navigate(APP_HOME, { replace: true }), REDIRECT_DELAY);
    return () => clearTimeout(timer);
  }, [status, navigate]);

  const content = {
    loading: {
      title: t('Confirming your email…'),
      text: t('This will only take a moment'),
    },
    success: {
      title: t('Email confirmed'),
      text: t("You're signed in, taking you to the app…"),
    },
    confirmed: {
      title: t('Email confirmed'),
      text: t('Sign in to start learning'),
    },
    error: {
      title: t("This link doesn't work"),
      text: t('The link is broken or has expired – request a new confirmation email and try again'),
    },
  }[status];

  const tone = status === 'loading' ? 'loading' : status === 'error' ? 'error' : 'done';

  return (
    <>
      <Header authUser={authUser} />
      <main className={css.verify}>
        <section className={css.verify__card}>
          <div
            key={status}
            className={`${css.verify__icon} ${css[`verify__icon--${tone}`]}`}
            aria-hidden="true"
          >
            {status === 'loading' && <span className={css.verify__spinner} />}
            {(status === 'success' || status === 'confirmed') && (
              <svg className={css.verify__glyph} viewBox="0 0 24 24" fill="none">
                <path
                  className={css.verify__check}
                  d="M5 12.5l4.5 4.5L19 7.5"
                  pathLength="1"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {status === 'error' && (
              <svg className={css.verify__glyph} viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 7l10 10M17 7L7 17"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
          <div className={css.verify__body} role="status" aria-live="polite">
            <h1 className={css.verify__title}>{content.title}</h1>
            <p className={css.verify__text}>{content.text}</p>
          </div>
          
          {status === 'success' && (
            <div className={css.verify__progress} aria-hidden="true">
              <span
                className={css['verify__progress-bar']}
                style={{ '--redirect-delay': `${REDIRECT_DELAY}ms` }}
              />
            </div>
          )}
          
          {status === 'success' && (
            <div className={css.verify__actions}>
              <Link to={APP_HOME} replace className={css.verify__btn}>
                {t('Continue to the app')}
              </Link>
            </div>
          )}
          
          {status === 'confirmed' && (
            <div className={css.verify__actions}>
              <Link to="/signin" className={css.verify__btn}>
                {t('Sign in')}
              </Link>
            </div>
          )}
          
          {status === 'error' && (
            <div className={css.verify__actions}>
              <Link to="/signin" className={css.verify__btn}>
                {t('Sign in')}
              </Link>
              <Link to="/" className={`${css.verify__btn} ${css['verify__btn--secondary']}`}>
                {t('Back to home')}
              </Link>
            </div>
          )}
        </section>
      </main>
    </>
  );
};

export default VerifyEmail;